// Conditions: a question or a section that only appears once an answer
// elsewhere on the form says it applies. "Would you like to take a photo?" →
// Yes opens the photo question; No, or no answer at all, keeps it out of the
// way.
//
// A condition sits on the entry it governs, question or section alike, and
// names the question whose answer decides:
//
//   { id: 'field_9', type: 'photo', label: 'Photo of the damage',
//     condition: { fieldId: 'field_4', value: 'Yes' } }
//
// What `value` holds — and so what "matches" means — follows the type of the
// question it names, read as that question is now rather than as it was when
// the condition was set:
//
//   Text, Contact   the answer to match, regardless of capitals or spacing. A
//                   contact matches on any one of its parts: name, phone,
//                   email or address.
//   Dropdown        one of its options; on a multi-answer dropdown, one of the
//                   options ticked.
//   Photo           true for "a photo was added", false for "no photo".
//   Number, Calc    a number, compared using `operator` (equals by default).
//   Date            the date, as the date input stores it (YYYY-MM-DD).
//
// A condition on a section governs everything inside it, subsections included,
// on top of any conditions of their own. A question nobody has answered meets
// no condition — except "no photo", which is exactly what an unanswered photo
// question is — and neither does a question that is itself hidden: an answer
// only counts while its question is on the form.
//
// A condition that can't be followed is ignored rather than obeyed, and the
// entry shows as though it had none: one whose question has been deleted,
// whose answer was never filled in or is no longer one of the options, or that
// closes a loop — a section waiting on a question inside itself, two questions
// each waiting on the other — where nothing could ever appear. Hiding a
// question for a reason nobody can see would lose it from the form, so the
// builder flags these instead and won't save until they're put right.
import { evaluateExpression, getReferencedFieldIds } from './formSchemaUtils';
import {
  findEntry,
  flattenFields,
  getSchemaEntries,
  getSchemaFields,
  isSection,
  sectionFields,
  sectionTitle,
} from './formSections';
import { CONTACT_PARTS, normalizeContactValue } from './contactValue';
import { hasPhotoValue } from './photoValue';
import { isMultiSelect, toSelectValues } from './selectValue';

// --- Reading a condition ---------------------------------------------------

export const conditionOf = (entry) =>
  (entry?.condition && typeof entry.condition === 'object' ? entry.condition : null);

// The kinds of answer a condition can wait on. Number and Calculation compare
// the same way; anything unrecognised is read as text.
export const conditionKind = (field) => {
  switch (field?.type) {
    case 'contact': return 'contact';
    case 'photo': return 'photo';
    case 'number':
    case 'calculation': return 'number';
    case 'select': return 'choice';
    case 'date': return 'date';
    default: return 'text';
  }
};

export const NUMBER_OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'ne', label: 'does not equal' },
  { value: 'gt', label: 'is more than' },
  { value: 'lt', label: 'is less than' },
  { value: 'gte', label: 'is at least' },
  { value: 'lte', label: 'is at most' },
];

// Comparisons allow for the rounding a calculation picks up along the way, so
// 0.1 + 0.2 still equals 0.3.
const nearly = (a, b) => Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));

const COMPARISONS = {
  eq: (a, b) => nearly(a, b),
  ne: (a, b) => !nearly(a, b),
  gt: (a, b) => a > b && !nearly(a, b),
  lt: (a, b) => a < b && !nearly(a, b),
  gte: (a, b) => a > b || nearly(a, b),
  lte: (a, b) => a < b || nearly(a, b),
};

// A condition set before its question became a number has no operator, and
// means "equals" until someone picks another.
export const conditionOperator = (condition) =>
  (COMPARISONS[condition?.operator] ? condition.operator : 'eq');

// What a condition starts as once its question is chosen — or, with no
// question yet, the moment the Conditional box is ticked. Every kind but photo
// still needs its answer filled in; a photo has only two, and "a photo was
// added" is the one an author almost always means.
export const newCondition = (field) => {
  const fieldId = field?.id || '';
  switch (conditionKind(field)) {
    case 'photo': return { fieldId, value: true };
    case 'number': return { fieldId, operator: 'eq', value: '' };
    default: return { fieldId, value: '' };
  }
};

// --- Matching an answer ----------------------------------------------------

const DATE_VALUE = /^(\d{4})-(\d{2})-(\d{2})$/;

const asText = (value) => (typeof value === 'string' || typeof value === 'number' ? String(value) : '');

// Text compares the way a person reads it: capitals and stray spaces don't make
// " yes" a different answer from "Yes". A blank condition matches nothing.
const tidyText = (value) => asText(value).trim().replace(/\s+/g, ' ').toLowerCase();
const matchesText = (answer, wanted) => tidyText(wanted) !== '' && tidyText(answer) === tidyText(wanted);

const toNumber = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string' || !value.trim()) return NaN;
  return Number(value);
};

// Whether `answer` — the current answer to the question a condition names —
// meets it.
export const answerMeets = (condition, field, answer) => {
  const wanted = condition?.value;
  switch (conditionKind(field)) {
    case 'photo':
      return typeof wanted === 'boolean' && hasPhotoValue(answer) === wanted;
    case 'number': {
      const actual = toNumber(answer);
      const target = toNumber(wanted);
      return !Number.isNaN(actual)
        && !Number.isNaN(target)
        && COMPARISONS[conditionOperator(condition)](actual, target);
    }
    case 'choice':
      return typeof wanted === 'string' && wanted !== '' && toSelectValues(answer).includes(wanted);
    case 'contact': {
      const contact = normalizeContactValue(answer);
      return CONTACT_PARTS.some((part) => matchesText(contact[part.key], wanted));
    }
    case 'date':
      return DATE_VALUE.test(asText(wanted)) && answer === wanted;
    default:
      return matchesText(answer, wanted);
  }
};

// --- What can stop a condition working -------------------------------------

const walkEntries = (entries, visit) => entries.forEach((entry) => {
  visit(entry);
  if (isSection(entry)) walkEntries(sectionFields(entry), visit);
});

// The questions an entry's condition decides about: the question itself, or
// every question anywhere inside the section.
const governedBy = (entry) => new Set(isSection(entry)
  ? flattenFields(sectionFields(entry)).map((field) => field.id)
  : [entry.id]);

// For each question, the questions it can't be settled without: the ones whose
// answers decide whether it shows — its own condition's, and those of every
// section it's filed in — and, for a calculation, the ones it's worked out
// from.
const dependencyGraph = (entries, fields) => {
  const graph = new Map();
  const walk = (list, inherited) => list.forEach((entry) => {
    const fieldId = conditionOf(entry)?.fieldId;
    const deciders = fieldId ? inherited.concat([fieldId]) : inherited;
    if (isSection(entry)) {
      walk(sectionFields(entry), deciders);
      return;
    }
    graph.set(entry.id, entry.type === 'calculation'
      ? deciders.concat(getReferencedFieldIds(entry.expression, fields))
      : deciders);
  });
  walk(entries, []);
  return graph;
};

// Whether following dependencies from `start` ever arrives at one of `targets`
// — `start` itself included.
const leadsTo = (graph, start, targets) => {
  const seen = new Set();
  const pending = [start];
  while (pending.length > 0) {
    const id = pending.pop();
    if (targets.has(id)) return true;
    if (!seen.has(id)) {
      seen.add(id);
      pending.push(...(graph.get(id) || []));
    }
  }
  return false;
};

// What every check below reads the form through: its questions in page order,
// numbered the way the builder numbers them, and what each one depends on.
const analyse = (entries) => {
  const fields = flattenFields(entries);
  return {
    fields,
    byId: new Map(fields.map((field) => [field.id, field])),
    numbers: new Map(fields.map((field, index) => [field.id, index + 1])),
    graph: dependencyGraph(entries, fields),
  };
};

const subjectOf = (entry) => (isSection(entry) ? 'section' : 'question');

// What stops a condition working, or null when nothing does. `incomplete`
// marks the ones that are merely unfinished — a question or an answer still to
// choose — which the editor's empty boxes already say for themselves.
const findProblem = (entry, condition, { byId, graph, numbers }) => {
  const unfinished = (message) => ({ message, incomplete: true });
  const broken = (message) => ({ message, incomplete: false });

  if (!condition.fieldId) return unfinished('Choose the question it depends on.');
  const field = byId.get(condition.fieldId);
  if (!field) return broken('The question it depended on has been deleted. Choose another.');

  const governed = governedBy(entry);
  const number = numbers.get(field.id);
  if (governed.has(field.id)) {
    return broken(isSection(entry)
      ? `Question ${number} is inside this section, so the section could never appear. Choose a question outside it.`
      : 'A question can’t depend on itself. Choose another.');
  }
  if (leadsTo(graph, field.id, governed)) {
    return broken(`Question ${number} depends on this ${subjectOf(entry)} in turn, so neither could ever appear. Choose another.`);
  }

  const { value } = condition;
  switch (conditionKind(field)) {
    case 'photo':
      return typeof value === 'boolean'
        ? null
        : unfinished('Choose whether it waits for a photo or for no photo.');
    case 'number':
      return Number.isNaN(toNumber(value)) ? unfinished('Enter the number to compare with.') : null;
    case 'choice': {
      if (typeof value !== 'string' || value === '') return unfinished('Choose the answer it waits for.');
      const options = Array.isArray(field.options) ? field.options : [];
      return options.includes(value)
        ? null
        : broken(`“${value}” is no longer one of the answers to question ${number}. Choose another.`);
    }
    case 'date':
      return DATE_VALUE.test(asText(value)) ? null : unfinished('Choose the date it waits for.');
    default:
      return asText(value).trim() ? null : unfinished('Enter the answer it waits for.');
  }
};

const formatDate = (value) => {
  const match = DATE_VALUE.exec(asText(value));
  if (!match) return asText(value);
  const [year, month, day] = match.slice(1).map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const operatorLabel = (condition) =>
  NUMBER_OPERATORS.find((operator) => operator.value === conditionOperator(condition)).label;

// The second half of "Q4 is “Yes”": what the question named has to say.
const describeAnswer = (condition, field) => {
  const { value } = condition;
  switch (conditionKind(field)) {
    case 'photo': return value === false ? 'has no photo' : 'has a photo';
    case 'number': return `${operatorLabel(condition)} ${asText(value)}`;
    case 'choice': return `${isMultiSelect(field) ? 'includes' : 'is'} “${asText(value)}”`;
    case 'date': return `is ${formatDate(value)}`;
    default: return `is “${asText(value)}”`;
  }
};

// Every condition on the form, in page order. Each comes with what stops it
// working (null when nothing does) and, once its question is chosen, that
// question, its number, and how the answer it waits for reads.
export const checkConditions = (entries) => {
  const context = analyse(entries);
  const checks = [];
  walkEntries(entries, (entry) => {
    const condition = conditionOf(entry);
    if (!condition) return;
    const field = context.byId.get(condition.fieldId) || null;
    checks.push({
      entry,
      problem: findProblem(entry, condition, context),
      field,
      number: field ? context.numbers.get(field.id) : null,
      answer: field ? describeAnswer(condition, field) : null,
    });
  });
  return checks;
};

// The questions a condition on `entryId` could wait on: every other question
// on the form, in page order, numbered as the builder numbers them and with
// the names of the sections they're filed in. Ones that would leave the entry
// waiting forever — inside the section the condition is on, or already
// waiting on the entry themselves — say why they can't be chosen.
export const conditionCandidates = (entries, entryId) => {
  const entry = findEntry(entries, entryId);
  if (!entry) return [];
  const { graph, numbers } = analyse(entries);
  const governed = governedBy(entry);
  const candidates = [];
  const walk = (list, sections) => list.forEach((item) => {
    if (isSection(item)) {
      walk(sectionFields(item), sections.concat([sectionTitle(item)]));
      return;
    }
    if (item.id === entry.id) return;
    let unavailable = null;
    if (governed.has(item.id)) unavailable = 'inside this section';
    else if (leadsTo(graph, item.id, governed)) unavailable = `depends on this ${subjectOf(entry)}`;
    candidates.push({ field: item, number: numbers.get(item.id), sections, unavailable });
  });
  walk(entries, []);
  return candidates;
};

// --- Filling a form in -----------------------------------------------------

const emptyAnswer = (field) => (isMultiSelect(field) ? [] : '');

// Whether a stored answer says anything at all.
const isAnswered = (field, value) => {
  switch (field.type) {
    case 'photo':
      return hasPhotoValue(value);
    case 'contact': {
      const contact = normalizeContactValue(value);
      return CONTACT_PARTS.some((part) => contact[part.key].trim() !== '');
    }
    case 'select':
      return toSelectValues(value).length > 0;
    default:
      return asText(value).trim() !== '';
  }
};

// Settle a form as it stands: which questions and sections their conditions
// hide, and what each calculation comes to. Returns `hiddenIds`, the ids of
// both, and `calculatedValues`, by calculation id.
//
// A hidden question's answer is left out of everything that reads it — the
// conditions that wait on it, and any calculation built from it — as though it
// had never been given. Switching "Yes" back to "No" hides the follow-up
// question, and takes whatever was typed into it out of the totals too.
//
// `keepAnswered` is for reading a submission back rather than filling one in:
// a question holding an answer shows whether or not its condition is still met
// — the form may have changed since — so a report never drops anything that
// was recorded, and a section shows when anything inside it does.
export const resolveForm = (schema, answers = {}, { keepAnswered = false } = {}) => {
  const entries = getSchemaEntries(schema);
  const context = analyse(entries);
  const { fields, byId } = context;

  // The conditions each entry has to meet: its own, when it can be followed,
  // and those of every section it's filed in.
  const gates = new Map();
  const collectGates = (list, inherited) => list.forEach((entry) => {
    const condition = conditionOf(entry);
    const followed = condition && !findProblem(entry, condition, context)
      ? inherited.concat([condition])
      : inherited;
    gates.set(entry.id, followed);
    if (isSection(entry)) collectGates(sectionFields(entry), followed);
  });
  collectGates(entries, []);

  // Each question settles once — whether it shows, and the answer it counts
  // as — however many conditions and calculations read it. Following the gates
  // never leads back round to the question being settled, since a condition
  // that would is one findProblem turns down; calculations can still reference
  // each other in a circle, though, and one caught up in that counts as
  // unanswered, the way it always has.
  const settled = new Map();
  const settling = new Set();

  const settle = (id) => {
    if (settled.has(id)) return settled.get(id);
    const field = byId.get(id);
    if (!field || settling.has(id)) return null;
    settling.add(id);
    const shown = gates.get(id).every(isMet) || (keepAnswered && isAnswered(field, answers[id]));
    let value = emptyAnswer(field);
    if (shown) value = field.type === 'calculation' ? calculate(field) : answers[id];
    settling.delete(id);
    const result = { shown, value };
    settled.set(id, result);
    return result;
  };

  const isMet = (condition) => {
    const decider = settle(condition.fieldId);
    return !!decider?.shown && answerMeets(condition, byId.get(condition.fieldId), decider.value);
  };

  const calculate = (field) => {
    const inputs = {};
    getReferencedFieldIds(field.expression, fields).forEach((id) => {
      const input = settle(id);
      if (input?.shown) inputs[id] = input.value;
    });
    const result = evaluateExpression(field.expression, fields, inputs);
    return result === null ? '' : String(result);
  };

  const hiddenIds = new Set();
  const calculatedValues = {};
  fields.forEach((field) => {
    const { shown, value } = settle(field.id);
    if (!shown) hiddenIds.add(field.id);
    if (field.type === 'calculation') calculatedValues[field.id] = value;
  });

  const settleSection = (section) => {
    const contentShown = sectionFields(section)
      .map((child) => (isSection(child) ? settleSection(child) : !hiddenIds.has(child.id)))
      .some(Boolean);
    const shown = gates.get(section.id).every(isMet) || (keepAnswered && contentShown);
    if (!shown) hiddenIds.add(section.id);
    return shown;
  };
  entries.forEach((entry) => {
    if (isSection(entry)) settleSection(entry);
  });

  return { hiddenIds, calculatedValues };
};

// The answers to send with a submission: everything entered, each calculation
// as it now stands, and nothing for a question its condition is hiding —
// whatever was put in before it was hidden again isn't part of this one.
export const answersToSubmit = (schema, answers, { hiddenIds, calculatedValues }) => {
  const data = { ...answers };
  getSchemaFields(schema).forEach((field) => {
    if (hiddenIds.has(field.id)) {
      data[field.id] = emptyAnswer(field);
    } else if (calculatedValues[field.id]) {
      data[field.id] = calculatedValues[field.id];
    }
  });
  return data;
};

// --- Keeping conditions in step with the builder ---------------------------

// Apply `revise` to every condition on the form, at any depth. What it leaves
// alone is handed back as it was, so nothing churns when nothing changed.
const reviseConditions = (entries, revise) => {
  let changed = false;
  const next = entries.map((entry) => {
    let updated = entry;
    const condition = conditionOf(entry);
    if (condition) {
      const revised = revise(condition);
      if (revised !== condition) updated = { ...updated, condition: revised };
    }
    if (isSection(entry)) {
      const fields = sectionFields(entry);
      const nested = reviseConditions(fields, revise);
      if (nested !== fields) updated = { ...updated, fields: nested };
    }
    if (updated !== entry) changed = true;
    return updated;
  });
  return changed ? next : entries;
};

// Renaming one of a dropdown's options takes the conditions that chose it
// along with it.
export const renameConditionAnswer = (entries, fieldId, from, to) =>
  reviseConditions(entries, (condition) => (
    condition.fieldId === fieldId && condition.value === from ? { ...condition, value: to } : condition
  ));
